import React, { useState } from 'react';
import { useToast } from '../components/ToastProvider';

interface QuestionnaireData {
  // Step 1: Basic Information
  businessName: string;
  businessType: string;
  websiteGoal: string;
  
  // Step 2: Technical Requirements
  websiteType: string;
  features: string[];
  integrations: string[];
  
  // Step 3: Design & Content
  designStyle: string;
  colors: string[];
  hasContent: string;
  contentHelp: string[];
  
  // Step 4: Timeline & Budget
  timeline: string;
  budget: string;
  additionalInfo: string;
}

const QuestionnairePage: React.FC = () => {
  const { addToast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<QuestionnaireData>({
    businessName: '',
    businessType: '',
    websiteGoal: '',
    websiteType: '',
    features: [],
    integrations: [],
    designStyle: '',
    colors: [],
    hasContent: '',
    contentHelp: [],
    timeline: '',
    budget: '',
    additionalInfo: ''
  });

  const totalSteps = 4;

  const businessTypes = [
    'E-commerce/Retail',
    'Professional Services',
    'Healthcare',
    'Technology/Software',
    'Restaurant/Food Service',
    'Real Estate',
    'Education',
    'Non-profit',
    'Photography/Creative',
    'Other'
  ];

  const websiteGoals = [
    'Generate leads/inquiries',
    'Sell products online',
    'Showcase portfolio/work',
    'Provide information/resources',
    'Build brand awareness',
    'Collect donations',
    'Book appointments/services',
    'Other'
  ];

  const websiteTypes = [
    'Business/Corporate Website',
    'E-commerce Store',
    'Portfolio/Creative Showcase',
    'Blog/Content Site',
    'Landing Page',
    'Membership/Community Site',
    'Booking/Appointment System',
    'Other'
  ];

  const availableFeatures = [
    'Contact Form',
    'Online Store',
    'Blog/News Section',
    'Photo Gallery',
    'Video Integration',
    'Social Media Integration',
    'Newsletter Signup',
    'Live Chat',
    'Online Booking',
    'Customer Login Area',
    'Search Functionality',
    'Multi-language Support'
  ];

  const designStyles = [
    'Modern & Minimalist',
    'Bold & Creative',
    'Professional & Corporate',
    'Elegant & Sophisticated',
    'Fun & Playful',
    'Classic & Traditional'
  ];

  const colorPreferences = [
    'Blue tones',
    'Green tones',
    'Red/Orange tones',
    'Purple tones',
    'Black & White',
    'Earth tones',
    'Bright & Vibrant',
    'Pastel colors'
  ];

  const handleInputChange = (field: keyof QuestionnaireData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleMultiSelectChange = (field: keyof QuestionnaireData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: (prev[field] as string[]).includes(value)
        ? (prev[field] as string[]).filter(item => item !== value)
        : [...(prev[field] as string[]), value]
    }));
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    // Basic validation
    if (!formData.businessName || !formData.businessType || !formData.websiteGoal) {
      addToast({
        type: 'error',
        title: 'Incomplete Form',
        message: 'Please fill in all required fields.',
      });
      return;
    }

    // Simulate API submission
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      addToast({
        type: 'success',
        title: 'Questionnaire Submitted!',
        message: 'Thank you for your detailed responses. We\'ll review your requirements and get back to you within 24 hours.',
        duration: 6000
      });

      // Reset form
      setFormData({
        businessName: '',
        businessType: '',
        websiteGoal: '',
        websiteType: '',
        features: [],
        integrations: [],
        designStyle: '',
        colors: [],
        hasContent: '',
        contentHelp: [],
        timeline: '',
        budget: '',
        additionalInfo: ''
      });
      setCurrentStep(1);

      setTimeout(() => {
        addToast({
          type: 'info',
          title: 'Note',
          message: 'Questionnaire data processing will be integrated in Phase 2 (Backend)',
          duration: 5000
        });
      }, 2000);

    } catch (error) {
      addToast({
        type: 'error',
        title: 'Submission Error',
        message: 'There was an error submitting your questionnaire. Please try again.',
      });
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-serenity-dark mb-6">Basic Information</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business/Organization Name *
              </label>
              <input
                type="text"
                value={formData.businessName}
                onChange={(e) => handleInputChange('businessName', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-serenity-blue focus:border-transparent"
                placeholder="Enter your business name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Type *
              </label>
              <select
                value={formData.businessType}
                onChange={(e) => handleInputChange('businessType', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-serenity-blue focus:border-transparent"
              >
                <option value="">Select your business type</option>
                {businessTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Primary Goal for Your Website *
              </label>
              <select
                value={formData.websiteGoal}
                onChange={(e) => handleInputChange('websiteGoal', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-serenity-blue focus:border-transparent"
              >
                <option value="">Select your primary goal</option>
                {websiteGoals.map(goal => (
                  <option key={goal} value={goal}>{goal}</option>
                ))}
              </select>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-serenity-dark mb-6">Technical Requirements</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Website Type
              </label>
              <select
                value={formData.websiteType}
                onChange={(e) => handleInputChange('websiteType', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-serenity-blue focus:border-transparent"
              >
                <option value="">Select website type</option>
                {websiteTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Features Needed (Select all that apply)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {availableFeatures.map(feature => (
                  <label key={feature} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.features.includes(feature)}
                      onChange={() => handleMultiSelectChange('features', feature)}
                      className="mr-2 text-serenity-blue focus:ring-serenity-blue"
                    />
                    <span className="text-sm">{feature}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Third-party Integrations Needed
              </label>
              <textarea
                value={formData.integrations.join(', ')}
                onChange={(e) => setFormData(prev => ({ ...prev, integrations: e.target.value.split(', ').filter(Boolean) }))}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-serenity-blue focus:border-transparent"
                rows={3}
                placeholder="e.g., Google Analytics, Mailchimp, Stripe, social media platforms..."
              />
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-serenity-dark mb-6">Design & Content</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Design Style
              </label>
              <select
                value={formData.designStyle}
                onChange={(e) => handleInputChange('designStyle', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-serenity-blue focus:border-transparent"
              >
                <option value="">Select design style</option>
                {designStyles.map(style => (
                  <option key={style} value={style}>{style}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Color Preferences (Select all that apply)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {colorPreferences.map(color => (
                  <label key={color} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.colors.includes(color)}
                      onChange={() => handleMultiSelectChange('colors', color)}
                      className="mr-2 text-serenity-blue focus:ring-serenity-blue"
                    />
                    <span className="text-sm">{color}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Do you have existing content (text, images, videos)?
              </label>
              <div className="space-y-2">
                {['Yes, I have everything ready', 'I have some content', 'No, I need help with content'].map(option => (
                  <label key={option} className="flex items-center">
                    <input
                      type="radio"
                      name="hasContent"
                      value={option}
                      checked={formData.hasContent === option}
                      onChange={(e) => handleInputChange('hasContent', e.target.value)}
                      className="mr-2 text-serenity-blue focus:ring-serenity-blue"
                    />
                    <span className="text-sm">{option}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-serenity-dark mb-6">Timeline & Budget</h2>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Timeline
              </label>
              <select
                value={formData.timeline}
                onChange={(e) => handleInputChange('timeline', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-serenity-blue focus:border-transparent"
              >
                <option value="">Select timeline</option>
                <option value="ASAP (Rush job)">ASAP (Rush job)</option>
                <option value="2-4 weeks">2-4 weeks</option>
                <option value="1-2 months">1-2 months</option>
                <option value="2-3 months">2-3 months</option>
                <option value="3+ months">3+ months</option>
                <option value="Flexible">Flexible</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Budget Range
              </label>
              <select
                value={formData.budget}
                onChange={(e) => handleInputChange('budget', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-serenity-blue focus:border-transparent"
              >
                <option value="">Select budget range</option>
                <option value="Under $1,000">Under $1,000</option>
                <option value="$1,000 - $2,500">$1,000 - $2,500</option>
                <option value="$2,500 - $5,000">$2,500 - $5,000</option>
                <option value="$5,000 - $10,000">$5,000 - $10,000</option>
                <option value="$10,000+">$10,000+</option>
                <option value="Need consultation">Need consultation</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Additional Information
              </label>
              <textarea
                value={formData.additionalInfo}
                onChange={(e) => handleInputChange('additionalInfo', e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-serenity-blue focus:border-transparent"
                rows={4}
                placeholder="Any additional requirements, questions, or information you'd like to share..."
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-serenity-dark mb-4">
            Service Questionnaire
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Help us understand your needs so we can create the perfect website for your business.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-serenity-blue">
              Step {currentStep} of {totalSteps}
            </span>
            <span className="text-sm font-medium text-serenity-blue">
              {Math.round((currentStep / totalSteps) * 100)}% Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-serenity-blue h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / totalSteps) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          {renderStep()}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                currentStep === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Previous
            </button>

            {currentStep < totalSteps ? (
              <button
                onClick={nextStep}
                className="px-6 py-3 bg-serenity-blue text-white rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200"
              >
                Next Step
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                className="px-8 py-3 bg-gradient-to-r from-serenity-blue to-blue-700 text-white rounded-lg font-medium hover:from-blue-700 hover:to-blue-800 transition-all duration-200 transform hover:scale-105"
              >
                Submit Questionnaire
              </button>
            )}
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-8 text-center">
          <p className="text-gray-600">
            Need help? Contact us at{' '}
            <a href="mailto:hello@serenityweb.com" className="text-serenity-blue hover:underline">
              hello@serenityweb.com
            </a>{' '}
            or call{' '}
            <a href="tel:+15551234567" className="text-serenity-blue hover:underline">
              (555) 123-4567
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuestionnairePage;
